import { ILeaseMaster } from 'src/models/dlcLease';

type ExpirationFields = Pick<
    ILeaseMaster,
    | 'leaseDate'
    | 'effDate'
    | 'term'
    | 'termUnits'
    | 'optionExercised'
    | 'opTerm'
    | 'opTermUnits'
    | 'addlOptionExercised'
    | 'addlOptionTerm'
    | 'addlOptionUnits'
>;

const parseDateOnly = (value?: Date | string): Date | null => {
    if (!value) {
        return null;
    }

    if (value instanceof Date) {
        return isNaN(value.getTime()) ? null : new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }

    const isoParts = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const displayParts = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    const year = isoParts ? Number(isoParts[1]) : displayParts ? Number(displayParts[3]) : NaN;
    const month = isoParts ? Number(isoParts[2]) : displayParts ? Number(displayParts[1]) : NaN;
    const day = isoParts ? Number(isoParts[3]) : displayParts ? Number(displayParts[2]) : NaN;
    const date = new Date(year, month - 1, day);

    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
};

const addCalendarTerm = (date: Date, amount: number, units?: string): Date | null => {
    if (!Number.isInteger(amount) || amount < 0) {
        return null;
    }

    const result = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (units === 'D') {
        result.setDate(result.getDate() + amount);
        return result;
    }

    if (units !== 'M' && units !== 'Y') {
        return null;
    }

    const originalDay = result.getDate();
    const targetMonth = result.getMonth() + (units === 'Y' ? amount * 12 : amount);
    result.setDate(1);
    result.setMonth(targetMonth);
    const lastDayOfTargetMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
    result.setDate(Math.min(originalDay, lastDayOfTargetMonth));
    return result;
};

const formatDateOnly = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const calculateLeaseExpirationDate = (lease: ExpirationFields): string | null => {
    const startDate = parseDateOnly(lease.effDate) || parseDateOnly(lease.leaseDate);
    if (!startDate || lease.term === null || lease.term === undefined) {
        return null;
    }

    const primaryExpiration = addCalendarTerm(startDate, lease.term, lease.termUnits);
    if (!primaryExpiration) {
        return null;
    }

    let expiration = primaryExpiration;

    if (lease.optionExercised === 'Y' && lease.opTerm !== null && lease.opTerm !== undefined && lease.opTermUnits) {
        expiration = addCalendarTerm(expiration, lease.opTerm, lease.opTermUnits) || expiration;
    }

    if (lease.addlOptionExercised === 'Y' && lease.addlOptionTerm !== null && lease.addlOptionTerm !== undefined && lease.addlOptionUnits) {
        expiration = addCalendarTerm(expiration, lease.addlOptionTerm, lease.addlOptionUnits) || expiration;
    }

    return formatDateOnly(expiration);
};



// WEBPACK FOOTER //
// ./src/components/leases/sections/leaseExpirationDate.ts